import { Search, X } from 'lucide-react';

import type { ProjectFilterOptions } from '../api/projects.api';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Select } from '@/shared/components/ui/select';
import { PROJECT_STATUSES, PROJECT_STATUS_LABELS } from '@/shared/constants/statuses';
import { isProjectStatus } from '@/shared/constants/statuses';
import type { ProjectFilters, ProjectStatus } from '@/shared/types/domain';

interface ProjectFilterBarProps {
  filters: ProjectFilters;
  onChange: (next: ProjectFilters) => void;
  /**
   * Coordenações e ministérios ao alcance da pessoa, já recortados pela RLS.
   * O seletor nunca nomeia algo fora do escopo dela — ele não pode virar um
   * oráculo da estrutura da filial para quem não a enxerga.
   */
  options: ProjectFilterOptions | undefined;
  /**
   * Situações oferecidas. O acervo de `/projetos` tira `draft` da lista: um
   * filtro que só pode devolver vazio é uma promessa que a tela não cumpre.
   */
  statuses?: readonly ProjectStatus[];
}

export function ProjectFilterBar({
  filters,
  onChange,
  options,
  statuses = PROJECT_STATUSES,
}: ProjectFilterBarProps) {
  const coordinations = options?.coordinations ?? [];
  const ministries = options?.ministries ?? [];

  // Escolher uma coordenação encolhe a lista de ministérios: oferecer os dez
  // ministérios da igreja depois de a pessoa ter dito "só Louvor e Adoração"
  // produz o combo que devolve vazio.
  const visibleMinistries = filters.coordinationId
    ? ministries.filter((ministry) => ministry.coordination_id === filters.coordinationId)
    : ministries;

  const hasFilters = Boolean(
    filters.coordinationId || filters.ministryId || filters.status || filters.search,
  );

  return (
    <div className="space-y-3">
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        <div className="relative">
          <Search
            aria-hidden="true"
            className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            type="search"
            className="h-9 pl-9"
            placeholder="Buscar pelo título"
            aria-label="Buscar projeto pelo título"
            value={filters.search ?? ''}
            onChange={(event) => onChange({ ...filters, search: event.target.value || undefined })}
          />
        </div>

        <Select
          aria-label="Filtrar por coordenação"
          value={filters.coordinationId ?? ''}
          onChange={(event) =>
            onChange({
              ...filters,
              coordinationId: event.target.value || undefined,
              // Trocar de coordenação invalida o ministério escolhido antes.
              ministryId: undefined,
            })
          }
        >
          <option value="">Todas as coordenações</option>
          {coordinations.map((coordination) => (
            <option key={coordination.id} value={coordination.id}>
              {coordination.name}
            </option>
          ))}
        </Select>

        <Select
          aria-label="Filtrar por ministério"
          value={filters.ministryId ?? ''}
          onChange={(event) =>
            onChange({ ...filters, ministryId: event.target.value || undefined })
          }
        >
          <option value="">Todos os ministérios</option>
          {visibleMinistries.map((ministry) => (
            <option key={ministry.id} value={ministry.id}>
              {ministry.name}
            </option>
          ))}
        </Select>

        <Select
          aria-label="Filtrar por situação"
          value={filters.status ?? ''}
          onChange={(event) => {
            const value = event.target.value;
            onChange({ ...filters, status: isProjectStatus(value) ? value : undefined });
          }}
        >
          <option value="">Todas as situações</option>
          {statuses.map((status) => (
            <option key={status} value={status}>
              {PROJECT_STATUS_LABELS[status]}
            </option>
          ))}
        </Select>
      </div>

      {/* O botão nasce junto com o primeiro filtro e some com o último. Entrar
          em `reveal` é o que o liga ao ato de filtrar, em vez de fazê-lo
          aparecer do nada embaixo dos campos. */}
      {/* A entrada vive numa `div` em volta, e não no próprio botão: o
          `duration-fast` que o botão carrega para o hover também vale como
          `animation-duration` (o plugin `tailwindcss-animate` estende a
          utilitária para as duas propriedades), e encurtaria o `reveal` de
          200ms para 110ms sem ninguém pedir. Vale para qualquer elemento que
          junte `animate-*` e `duration-*` na mesma classe. */}
      {hasFilters ? (
        <div className="animate-reveal">
          <Button variant="ghost" size="sm" onClick={() => onChange({})}>
            <X className="size-4" aria-hidden="true" />
            Limpar filtros
          </Button>
        </div>
      ) : null}
    </div>
  );
}
